import { useQuery } from '@tanstack/react-query';
import api from './api';
import queryClient from './queryClient';

export const BADGE_KEYS = {
  SELLER_REQUESTS: 'sellerRequests',
  CONTACTS: 'contacts',
  MESSAGES: 'messages',
  NOTIFICATIONS: 'notifications',
  PRO_REGISTRATIONS: 'proRegistrations',
  PURCHASE_REQUESTS: 'purchaseRequests',
  SERVICE_REQUESTS: 'serviceRequests',
} as const;

type BadgeKey = (typeof BADGE_KEYS)[keyof typeof BADGE_KEYS];
type BadgeCounts = Record<BadgeKey, number>;

const QUERY_KEY = ['badges'];
const REFETCH_INTERVAL = 300_000;

interface BadgeEndpointConfig {
  endpoint: string;
  extractCount: (data: any) => number;
}

const API_ENDPOINTS: Record<string, BadgeEndpointConfig> = {
  [BADGE_KEYS.SELLER_REQUESTS]: { endpoint: '/admin/seller-requests/count', extractCount: (d) => d.count ?? 0 },
  [BADGE_KEYS.CONTACTS]: { endpoint: '/contacts/admin/count-unread', extractCount: (d) => d.unread_count ?? 0 },
  [BADGE_KEYS.MESSAGES]: { endpoint: '/direct-messages/unread-count', extractCount: (d) => d.unread_count ?? 0 },
  [BADGE_KEYS.NOTIFICATIONS]: { endpoint: '/notifications?limit=1', extractCount: (d) => d.unread_count ?? 0 },
  [BADGE_KEYS.PURCHASE_REQUESTS]: {
    endpoint: '/admin/purchase-requests/count-pending',
    extractCount: (d) => d.count ?? 0,
  },
  [BADGE_KEYS.SERVICE_REQUESTS]: {
    endpoint: '/admin/service-requests/count-pending',
    extractCount: (d) => d.count ?? 0,
  },
};

const initialCounts: BadgeCounts = {
  [BADGE_KEYS.SELLER_REQUESTS]: 0,
  [BADGE_KEYS.CONTACTS]: 0,
  [BADGE_KEYS.MESSAGES]: 0,
  [BADGE_KEYS.NOTIFICATIONS]: 0,
  [BADGE_KEYS.PRO_REGISTRATIONS]: 0,
  [BADGE_KEYS.PURCHASE_REQUESTS]: 0,
  [BADGE_KEYS.SERVICE_REQUESTS]: 0,
};

async function fetchBadgeCounts(): Promise<BadgeCounts> {
  const endpoints = badgeService._isAdmin
    ? Object.entries(API_ENDPOINTS)
    : Object.entries(API_ENDPOINTS).filter(([key]) =>
        ([BADGE_KEYS.MESSAGES, BADGE_KEYS.NOTIFICATIONS] as string[]).includes(key)
      );

  const results = await Promise.allSettled(
    endpoints.map(async ([key, config]) => {
      const response = await api.get(config.endpoint);
      return { key, count: config.extractCount(response.data) };
    })
  );

  const counts = { ...initialCounts };
  results.forEach((result) => {
    if (result.status === 'fulfilled') {
      counts[result.value.key as BadgeKey] = result.value.count;
    }
  });
  return counts;
}

function useBadgeQuery() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchBadgeCounts,
    refetchInterval: REFETCH_INTERVAL,
    refetchOnWindowFocus: true,
    staleTime: REFETCH_INTERVAL,
  });
}

export function useBadges() {
  const { data: counts = initialCounts, isLoading } = useBadgeQuery();
  return {
    counts,
    isLoading,
    sellerRequests: counts[BADGE_KEYS.SELLER_REQUESTS],
    contacts: counts[BADGE_KEYS.CONTACTS],
    messages: counts[BADGE_KEYS.MESSAGES],
    notifications: counts[BADGE_KEYS.NOTIFICATIONS],
    purchaseRequests: counts[BADGE_KEYS.PURCHASE_REQUESTS],
    serviceRequests: counts[BADGE_KEYS.SERVICE_REQUESTS],
  };
}

export function useBadgeCount(key: BadgeKey): number {
  const { data: counts = initialCounts } = useBadgeQuery();
  return counts[key] ?? 0;
}

function updateCache(updater: (counts: BadgeCounts) => BadgeCounts) {
  queryClient.setQueryData<BadgeCounts>(QUERY_KEY, (old) => {
    if (!old) return old as unknown as BadgeCounts;
    return updater(old);
  });
}

export const badgeService = {
  _isAdmin: false,

  init({ isAdmin = false } = {}) {
    this._isAdmin = isAdmin;
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  },

  destroy() {
    this._isAdmin = false;
    queryClient.removeQueries({ queryKey: QUERY_KEY });
  },

  setCount(key: BadgeKey, value: number) {
    updateCache((counts) => ({ ...counts, [key]: Math.max(0, value) }));
  },

  increment(key: BadgeKey, amount: number = 1) {
    updateCache((counts) => ({ ...counts, [key]: (counts[key] ?? 0) + amount }));
  },

  decrement(key: BadgeKey, amount: number = 1) {
    updateCache((counts) => ({ ...counts, [key]: Math.max(0, (counts[key] ?? 0) - amount) }));
  },

  reset(key?: BadgeKey) {
    if (key) {
      updateCache((counts) => ({ ...counts, [key]: 0 }));
    } else {
      queryClient.setQueryData(QUERY_KEY, { ...initialCounts });
    }
  },

  sync() {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  },

  async syncOne(key: BadgeKey) {
    const config = API_ENDPOINTS[key];
    if (!config) return;
    try {
      const response = await api.get(config.endpoint);
      const count = config.extractCount(response.data);
      this.setCount(key, count);
    } catch (err: any) {
      console.debug(`[BadgeService] Failed to sync ${key}:`, err.message);
    }
  },

  markSellerRequestProcessed() {
    this.decrement(BADGE_KEYS.SELLER_REQUESTS);
  },
  markContactRead() {
    this.decrement(BADGE_KEYS.CONTACTS);
  },
  markMessageRead() {
    this.decrement(BADGE_KEYS.MESSAGES);
  },
  markNotificationRead() {
    this.decrement(BADGE_KEYS.NOTIFICATIONS);
  },
  markAllNotificationsRead() {
    this.reset(BADGE_KEYS.NOTIFICATIONS);
  },
  onNewSellerRequest() {
    this.increment(BADGE_KEYS.SELLER_REQUESTS);
  },
  onNewMessage() {
    this.increment(BADGE_KEYS.MESSAGES);
  },
  onNewNotification() {
    this.increment(BADGE_KEYS.NOTIFICATIONS);
  },
};

export { BADGE_KEYS as BadgeKeys };
export default badgeService;
